import { useEffect, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonList, IonItem, IonInput, IonLabel, IonButton, IonToggle, IonSelect, IonSelectOption,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/auth.store';
import { api, apiErrorMessage } from '@/lib/api';
import { toast } from '@/store/ui.store';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function SettingsPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const [name, setName] = useState(user?.name ?? '');
  const [timezone, setTimezone] = useState(user?.timezone ?? 'UTC');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [weeklyReport, setWeeklyReport] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(user?.name ?? '');
    setTimezone(user?.timezone ?? 'UTC');
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await updateProfile({ name: name.trim() || undefined, timezone });
      toast(t('common.success'), 'success');
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{t('settings.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-shell page-shell--flush">
          <h6 className="section-title">{t('settings.profile')}</h6>
          <IonList inset>
            <IonItem><IonInput label={t('auth.email')} labelPlacement="stacked" value={user?.email} disabled /></IonItem>
            <IonItem><IonInput label={t('auth.name')} labelPlacement="stacked" value={name}
              onIonInput={(event) => setName((event.target as HTMLIonInputElement).value as string)} /></IonItem>
            <IonItem>
              <IonSelect label={t('settings.timezone')} interface="popover" value={timezone}
                onIonChange={(event) => setTimezone(event.detail.value as string)}>
                {['UTC', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney']
                  .map((zone) => <IonSelectOption key={zone} value={zone}>{zone}</IonSelectOption>)}
              </IonSelect>
            </IonItem>
            <LanguageSwitcher />
          </IonList>

          <h6 className="section-title">{t('settings.notifications')}</h6>
          <IonList inset>
            <IonItem><IonToggle checked={emailNotifications} onIonChange={(event) => setEmailNotifications(event.detail.checked)}>
              {t('settings.emailNotifications')}</IonToggle></IonItem>
            <IonItem><IonToggle checked={weeklyReport} onIonChange={(event) => setWeeklyReport(event.detail.checked)}>
              {t('settings.weeklyReport')}</IonToggle></IonItem>
          </IonList>

          <div style={{ padding: '0 14px' }}>
            <IonButton expand="block" disabled={busy} onClick={() => void save()}>
              {busy ? '…' : t('common.save')}
            </IonButton>
          </div>

          <h6 className="section-title">{t('settings.billing')}</h6>
          <IonList inset>
            <IonItem><IonLabel><h3>{user?.plan?.toUpperCase() ?? 'FREE'}</h3><p>{t('settings.dailyLimits')}</p></IonLabel></IonItem>
          </IonList>
        </div>
      </IonContent>
    </IonPage>
  );
}
